<template>
  <div class="phone-preview-container">
    <div class="phone-wrapper">
      <img src="@/assets/img/iframe.png" v-if="showImage" />
      <iframe
        v-if="visible"
        :src="url"
        frameborder="0"
        id="iframe"
        ref="iframeRef"
        sandbox="allow-scripts allow-same-origin allow-forms"
        allow="local-network-access *"
      ></iframe>
    </div>
  </div>
</template>

<script setup>
import { useEnv } from '@/utils/useEnv'
import { useEventBus } from '@/utils/useUtils.js'
import { debounce } from 'lodash'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { getDefaultConfigByEnv } from './preview.js'

const $eventBus = useEventBus()
const { env } = useEnv()
const iframeRef = ref(null)

defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
})


const currentCity = ref({
  headerTitle: '',
  cityCode: '',
  cityName: '',
  firstLetter: '',
  productBasicInfo: [
    {
      tenantId: '',
      cheWuTenantId: '',
      goodsGroupId: '',
      businessIds: [],
      brand: '',
      brandId: '',
      seriesId: '',
      carSeries: '',
      carType: '',
      productName: '',
      nameFirstLetter: 'K',
      qualificationInfo: '',
      qualificationInfoName: '',
      cityCode: '',
      topTags: [],
      pictureUrl: '',
      bottomTags: [],
      carManageYears: '',
      rentPlans: [],
    },
  ],
})

// 根据环境获取 lowcode host
const lowcodeHostMap = {
  dev: 'https://lowcode-daily.cheweijia.com',
  test: 'https://lowcode-daily.cheweijia.com',
  test2: 'https://lowcode-daily2.cheweijia.com',
  gray: 'https://lowcode-gray.cheweijia.com',
  prod: 'https://lowcode.cheweijia.com',
}

const host = computed(() => lowcodeHostMap[env.value])
const url = computed(() => `${host.value}/lowcode/previewMobile.html`)

onBeforeUnmount(() => {
  currentCity.value = {
    headerTitle: '',
    cityCode: '',
    cityName: '',
    firstLetter: '',
    productBasicInfo: [
      {
        tenantId: '',
        cheWuTenantId: '',
        goodsGroupId: '',
        businessIds: [],
        brand: '',
        brandId: '',
        seriesId: '',
        carSeries: '',
        carType: '',
        productName: '',
        nameFirstLetter: 'K',
        qualificationInfo: '',
        qualificationInfoName: '',
        cityCode: '',
        topTags: [],
        pictureUrl: '',
        bottomTags: [],
        carManageYears: '',
        rentPlans: [],
      },
    ],
  }
  $eventBus.$off('iframeDataChange')
  $eventBus.$off('iframePlanChange')
  $eventBus.$off('iframeCompanyChange')
})


// 使用原生 postMessage 发送消息
const postMessageToIframe = debounce((data) => {
  const iframe = iframeRef.value
  if (!iframe?.contentWindow) return

  iframe.contentWindow.postMessage(
    data,
    host.value,
  )
}, 200)

const plans = ref([])
const companyInfo = ref(null)
const carInfo = ref(null)
const emits = defineEmits(['setPreviewLoading'])

const showImage = computed(() => {
  return !(plans.value?.length > 0 || companyInfo.value || carInfo.value)
})

const transferData = async () => {
  transferCarData(carInfo.value)
  transferPlanData(plans.value)
  transferCompanyData(companyInfo.value)
}

watch(
  [() => plans.value, () => companyInfo.value, () => carInfo.value],
  async ([a, b, c]) => {
    if (!a?.length && !b && !c) return
    transferData()
    postMessageToIframe({
      type: 'UPDATE_DATA',
      payload: getDefaultConfigByEnv(env.value)(currentCity.value),
    })
  },
  {
    deep: true,
    immediate: true,
  },
)


// * @property {number} ELECTRIC - 1 纯电动
//  * @property {number} GAS - 2 汽油
//  * @property {number} PLUGINHYBIRDELECT - 3 插电式混动
//  * @property {number} REEV - 4 增程式混动
//  * @property {number} HYBIRDELECT - 5 油电混动
//  * @property {number} DIESEL - 6 柴油
//  * @property {number} GASOLINE - 7 天然气
//  * @property {number} OTHER - 8 其他
// 格式化数据
const transferCarData = (data) => {
  // 如果传入了 productBasicInfo 数组（批量上架场景），使用深拷贝避免循环引用
  if (data?.productBasicInfo && Array.isArray(data.productBasicInfo)) {
    // 使用 JSON 序列化做深拷贝，完全解除响应式引用
    try {
      currentCity.value.productBasicInfo = JSON.parse(
        JSON.stringify(data.productBasicInfo),
      )
    } catch (e) {
      // 如果序列化失败（如包含循环引用），使用空数组
      // eslint-disable-next-line no-console
      console.error('Failed to clone productBasicInfo:', e)
      currentCity.value.productBasicInfo = []
    }
    // 同时更新 header 信息
    currentCity.value.headerTitle = data?.operationTypeName
    currentCity.value.cityCode = data?.cityCode
    currentCity.value.cityName = data?.cityName || '--'
    return
  }

  // 单编辑场景：处理单个商品
  const productName =
    `${data?.brandName || '--'}·${data?.seriesName || ''} ${data?.modelYear || '--'}款` || '暂无'
  const topTags = []
  if (data?.ageName) {
    topTags.push(data?.ageName)
  }
  if (data?.pureElectricDrivingRange && [1, 3, 4].includes(data?.energyType)) {
    topTags.push(`续航${data?.pureElectricDrivingRange}公里`)
  }
  if (data?.fuelConsumption && [2, 5, 6, 7].includes(data?.energyType)) {
    topTags.push(`百公里油耗${data?.fuelConsumption}L`)
  }
  if (data?.carCert && data?.carCertName) {
    topTags.push(data?.carCertName)
  }

  let pictureUrl = ''
  if (data?.carModelPics?.length) {
    pictureUrl = data.carModelPics[0].value || ''
  }

  currentCity.value.headerTitle = data?.operationTypeName
  currentCity.value.cityCode = data?.cityCode
  currentCity.value.cityName = data?.cityName || '--'
  currentCity.value.headerTitle = data?.operationTypeName

  const { productBasicInfo } = currentCity.value
  if (!productBasicInfo?.length) return

  productBasicInfo[0] = {
    ...productBasicInfo[0],
    brand: data?.brandName,
    brandId: data?.brandId,
    seriesId: data?.seriesId,
    carSeries: data?.seriesName,
    carType: data?.operationType,
    productName,
    nameFirstLetter: 'K',
    qualificationInfo: data?.carLevel,
    qualificationInfoName: data?.operationType === 10 ? data?.carLevelName : null,
    cityCode: data?.cityCode,
    topTags,
    pictureUrl,
  }
  currentCity.value.productBasicInfo = productBasicInfo
}
const transferPlanData = (data = []) => {
  const { productBasicInfo } = currentCity.value
  if (!productBasicInfo?.length) return

  // 只有当 data 非空时才更新（批量上架场景下每个商品已有 rentPlans，不需要覆盖）
  if (data.length === 0) return

  productBasicInfo[0].rentPlans = (data).map((plan) => ({
    goodsId: plan.id,
    rentPeriod: plan.rentPeriod,
    rentPeriodType: plan.rentPeriodType,
    rentPeriodShowName: `${plan.rentPeriod}${plan.rentPeriodType === 1 ? '天' : '个月'}`,
    presentDays: plan.presentDays,
    rentAmountUnit: plan.rentAmountUnit,
    rentAmount: plan.rentAmount,
    depositPayMode: 6,
    deposit: plan.deposit,
    rentCategory: 1,
    state: plan.state,
  }))
}

const transferCompanyData = (data) => {
  if (!data) return
  const { productBasicInfo } = currentCity.value
  if (!productBasicInfo?.length) return
  const bottomTags = []
  const { cooperationPlatformDesc = [], businessYears } = data

  if (cooperationPlatformDesc?.length) {
    cooperationPlatformDesc.forEach((item) => {
      bottomTags.push(item)
    })
  }

  productBasicInfo[0].bottomTags = bottomTags?.filter((item) => item) || []
  productBasicInfo[0].carManageYears = businessYears
  currentCity.value.productBasicInfo = productBasicInfo
}

onMounted(() => {
  const iframe = iframeRef.value
  if (iframe) {
    window.addEventListener('message', (event) => {
      if (event.origin !== host.value) return
      if (event.data && event.data.type === 'IFRAME_READY') {
        emits('setPreviewLoading', true)
        postMessageToIframe({
          type: 'UPDATE_DATA',
          payload: getDefaultConfigByEnv(env.value)(currentCity.value),
        })
      }
    })

    // 添加加载失败处理
    iframe.onerror = () => {
      emits('setPreviewLoading', true)
    }

    // 添加超时处理
    const timeoutId = setTimeout(() => {
      emits('setPreviewLoading', true)
    }, 10000) // 10秒超时

    iframe.onload = () => {
      clearTimeout(timeoutId) // 清除超时定时器

      $eventBus.$on('iframeDataChange', (data) => {
        // 清除预览数据（从第三步返回时触发）
        if (!data) {
          carInfo.value = null
          return
        }
        carInfo.value = { ...data }
      })

      $eventBus.$on('iframePlanChange', (data) => {
        plans.value = [...data]
      })

      $eventBus.$on('iframeCompanyChange', (data) => {
        companyInfo.value = { ...data }
      })
    }
  }
})
</script>

<style lang="scss" scoped>
.phone-preview-container {
  width: 289px; // 375 * 0.747 = 280.125，再增加一些空间（大约9px）给竖向滚动条
  position: relative;
  flex-shrink: 0;
  overflow-y: auto;
}

.phone-wrapper {
  width: 375px;
  position: absolute;
  height: 812px;
  transform-origin: 0 0;
  transform: scale(0.747);
  background: #fff;
  border-radius: 16px;
  border: 4px solid #5e5e5e;
  box-sizing: border-box;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
  }

  iframe {
    width: 100%;
    height: 100%; // 恢复为全高
    border: none;
  }
}
</style>
